import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, User2, Rocket } from "lucide-react";
import Logo from "../components/Logo";
import LanguageToggle from "../components/LanguageToggle";
import AppGrid from "../components/AppGrid";
import AdminUsers from "../components/AdminUsers";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { personalApps, companyApps } from "../i18n/apps";

export default function Dashboard() {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    navigate("/");
    logout();
  };

  return (
    <div className="min-h-screen bg-u-bg text-u-ink">
      <div className="mesh-glow w-[500px] h-[500px] bg-u-purple/12 -top-40 right-0" />

      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200/70">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-4 flex items-center justify-between">
          <Logo size={32} />
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <div className="hidden sm:flex items-center gap-2 rounded-full bg-white border border-slate-200 px-3.5 py-2 text-sm shadow-sm">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-u-purple/15 text-u-purple">
                <User2 size={14} />
              </span>
              <span data-testid="dashboard-username" className="text-slate-700 font-medium">
                {user?.name || user?.email}
              </span>
            </div>
            <button
              onClick={handleLogout}
              data-testid="dashboard-logout"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:text-u-ink hover:border-slate-300 transition-all"
            >
              <LogOut size={15} /> <span className="hidden sm:inline">{t.nav.logout}</span>
            </button>
          </div>
        </div>
      </header>

      <main className="relative mx-auto max-w-7xl px-5 sm:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          data-testid="demo-banner"
          className="mb-8 flex items-start gap-3 rounded-2xl border border-u-purple/25 bg-u-purple/8 px-5 py-4"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-u-purple/15 text-u-purple">
            <Rocket size={17} />
          </span>
          <div>
            <p className="font-display font-semibold text-u-ink">{t.dashboard.demoTitle}</p>
            <p className="text-sm text-slate-600 mt-0.5">{t.dashboard.demoText}</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-12"
        >
          <p className="text-u-purple text-sm font-semibold uppercase tracking-[0.2em] mb-2">
            PiD Dashboard
          </p>
          <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tight text-u-ink">
            {t.dashboard.welcome}, {user?.name?.split(" ")[0] || user?.email}
          </h1>
        </motion.div>

        {/* Admin: registered users */}
        {user?.role === "admin" && <AdminUsers />}

        {/* Personal Apps */}
        <section className="mb-16">
          <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-u-ink">{t.dashboard.personalApps}</h2>
              <p className="text-slate-500 mt-1">{t.dashboard.personalSubtitle}</p>
            </div>
            <span className="text-xs rounded-full border border-slate-200 bg-white px-3 py-1.5 text-slate-500">
              {t.dashboard.comingSoon}
            </span>
          </div>
          <AppGrid apps={personalApps} testidPrefix="dash-personal" />
        </section>

        {/* Company Apps */}
        <section>
          <div className="flex items-end justify-between mb-6 flex-wrap gap-2">
            <div>
              <h2 className="font-display text-2xl md:text-3xl font-semibold text-u-ink">{t.dashboard.companyApps}</h2>
              <p className="text-slate-500 mt-1">{t.dashboard.companySubtitle}</p>
            </div>
            <span className="text-xs rounded-full border border-slate-200 bg-white px-3 py-1.5 text-slate-500">
              {t.dashboard.comingSoon}
            </span>
          </div>
          <AppGrid apps={companyApps} testidPrefix="dash-company" />
        </section>
      </main>
    </div>
  );
}
